import { SelectOption } from '@ardoq/select';
import { SurveyReferenceQuestionProps } from './referenceTypes';
import { ReferenceAnswerValue } from '../../types';
import { getReferenceOrTagQuestionUserFeedback } from '../getUserFeedback';
import {
  getRangeValidator,
  noAccessReferencesMeetMaxRangeValidation,
  checkShouldFetchReferenceSuggestions,
} from '../questionOperations';
import { SurveyQuestionOptions } from '../QuestionOptions/SurveyQuestionOptions';
import { SurveyListReferencesWithQuestions } from '../ReferenceQuestionWithQuestions/SurveyListReferencesWithQuestions';
import {
  createComponentForReferenceAnswer,
  createReferenceFromOption,
} from './referenceQuestionUtils';
import { SurveyListReferencesQuestion } from './SurveyListReferencesQuestion';
import { SurveySingleSelectReferenceQuestion } from './SurveySingleSelectReferenceQuestion';
import { AI_SUGGESTED_OPTION_ID } from '../../consts';

export const SurveyReferenceQuestion = ({
  question,
  answer,
  readonly,
  value,
  hasSubQuestions,
  survey,
  componentId,
  fields,
  currentLocale,
  i18nLocale,
  hasAttemptedSubmission,
  hasAutocompleteFeature,
  hasReferenceSuggestionsFeature,
  hasEditableReferencesInQuestionsFeature,
  noAccessReferences = 0,
  isEmbedded,
  hideReadOnlyLabel,
  highlightUnansweredPriorityQuestions,
  setAnswer,
  addNestedAnswer,
  setNestedAnswer,
  removeNestedAnswer,
  updateReferenceComponent,
  getDuplicate,
  getExistingComponentByName,
  generateComponentDescription,
  trackDescriptionGenerated,
  trackDescriptionGeneratedFailed,
  trackCreatedReferenceFromQuestion,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  userOptions,
  QuestionContentWrapper,
}: SurveyReferenceQuestionProps) => {
  const outgoing = question.properties.outgoing;
  const questionId = question._id;
  const validators = question.validators;
  const questions = question.properties.questions;
  const allowComponentCreation = question.properties.allowComponentCreation;
  const questionType = question.type;
  const aiSuggestionsEnabled = question.aiSuggestionsEnabled;
  const allowMultipleReferences = question.properties.allowMultipleReferences;
  const identifierFieldName = question.properties.additionalIdentifier;
  const preventReferenceModification =
    question.properties.preventReferenceModification;
  const rangeValidator = getRangeValidator(validators);

  if (noAccessReferencesMeetMaxRangeValidation(noAccessReferences, validators))
    return null;

  const shouldFetchReferenceSuggestions = checkShouldFetchReferenceSuggestions(
    hasReferenceSuggestionsFeature,
    aiSuggestionsEnabled
  );

  const createReference = (
    refOption: SelectOption<string>[] | SelectOption<string>,
    prepend?: boolean
  ) => {
    if (!refOption) return;
    const selectedOption = Array.isArray(refOption) ? refOption[0] : refOption;
    if (shouldFetchReferenceSuggestions) {
      trackCreatedReferenceFromQuestion(
        selectedOption.testId === AI_SUGGESTED_OPTION_ID
      );
    }
    addNestedAnswer({
      questionId,
      nestedAnswer: createReferenceFromOption(selectedOption, outgoing),
      prepend,
    });
  };

  const deleteReference = (ref: ReferenceAnswerValue) => {
    return removeNestedAnswer({
      questionId,
      index: value.indexOf(ref),
    });
  };

  const editReference = (
    ref: ReferenceAnswerValue,
    componentOption: SelectOption<string>
  ) => {
    const index = value.indexOf(ref);
    updateReferenceComponent({
      questionId,
      index,
      component: createComponentForReferenceAnswer(componentOption),
    });
  };

  const noExistingRefToComponent = (option: SelectOption<string>) => {
    return !value.some(({ component }) => {
      const isNewOption = option.value === option.label;
      return (
        component._id === option.value ||
        (isNewOption && component.name === option.value)
      );
    });
  };

  const additionalFilterOptions = (option: SelectOption<string>) => {
    if (allowMultipleReferences) return true;
    return noExistingRefToComponent(option);
  };

  const showSingleSelect = Boolean(
    rangeValidator &&
    !hasSubQuestions &&
    rangeValidator.max === 1 &&
    value.length <= 1
  );

  const userFeedback = getReferenceOrTagQuestionUserFeedback({
    value,
    rangeValidator,
    questionType,
    noAccessReferences,
    i18nLocale,
  });

  const totalResponses = value.length + noAccessReferences;

  if (showSingleSelect) {
    const selectedComponent = value.length ? value[0].component : undefined;
    return (
      <SurveySingleSelectReferenceQuestion
        selectedComponent={selectedComponent}
        question={question}
        answer={answer}
        questionLabel={question.label}
        readonly={readonly}
        surveyId={survey._id}
        questionId={questionId}
        componentId={componentId}
        userFeedback={userFeedback}
        fields={fields}
        identifierFieldName={identifierFieldName}
        shouldFetchReferenceSuggestions={shouldFetchReferenceSuggestions}
        allowComponentCreation={allowComponentCreation}
        outgoing={outgoing}
        currentLocale={currentLocale}
        i18nLocale={i18nLocale}
        hideReadOnlyLabel={hideReadOnlyLabel}
        QuestionContentWrapper={QuestionContentWrapper}
        highlightUnansweredPriorityQuestions={
          highlightUnansweredPriorityQuestions
        }
        setAnswer={setAnswer}
        additionalFilterOptions={additionalFilterOptions}
        getAISuggestions={getAISuggestions}
        fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
      />
    );
  }

  if (hasSubQuestions) {
    return (
      <QuestionContentWrapper
        question={question}
        answer={answer}
        highlightUnansweredPriorityQuestions={
          highlightUnansweredPriorityQuestions
        }
        i18nLocale={i18nLocale}
      >
        <SurveyListReferencesWithQuestions
          userOptions={userOptions}
          survey={survey}
          componentId={componentId}
          parentId={questionId}
          questionId={questionId}
          questionLabel={question.label}
          references={value}
          questions={questions}
          readonly={readonly}
          fields={fields}
          preventReferenceModification={preventReferenceModification}
          currentLocale={currentLocale}
          i18nLocale={i18nLocale}
          input={
            <SurveyQuestionOptions
              isDisabled={Boolean(
                rangeValidator &&
                rangeValidator.max &&
                totalResponses >= rangeValidator.max
              )}
              isCreatable={allowComponentCreation}
              preventReferenceModification={preventReferenceModification}
              currentLocale={currentLocale}
              i18nLocale={i18nLocale}
              surveyId={survey._id}
              questionId={questionId}
              componentId={componentId}
              userFeedback={userFeedback}
              fields={fields}
              identifierFieldName={identifierFieldName}
              questionLabel={question.label}
              shouldFetchReferenceSuggestions={shouldFetchReferenceSuggestions}
              onChange={(
                refOption: SelectOption<string> | SelectOption<string>[] | null
              ) => {
                if (!refOption) return;
                createReference(refOption, true);
              }}
              additionalFilterOptions={additionalFilterOptions}
              getAISuggestions={getAISuggestions}
              fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
            />
          }
          hasAttemptedSubmission={hasAttemptedSubmission}
          identifierFieldName={identifierFieldName}
          hasAutocompleteFeature={hasAutocompleteFeature}
          hasEditableReferencesInQuestionsFeature={
            hasEditableReferencesInQuestionsFeature
          }
          hideReadOnlyLabel={hideReadOnlyLabel}
          QuestionContentWrapper={QuestionContentWrapper}
          allowComponentCreation={Boolean(allowComponentCreation)}
          isEmbedded={isEmbedded}
          highlightUnansweredPriorityQuestions={
            highlightUnansweredPriorityQuestions
          }
          deleteReference={deleteReference}
          editReference={editReference}
          getDuplicate={getDuplicate}
          getExistingComponentByName={getExistingComponentByName}
          additionalFilterOptions={additionalFilterOptions}
          setNestedAnswer={setNestedAnswer}
          generateComponentDescription={generateComponentDescription}
          trackDescriptionGenerated={trackDescriptionGenerated}
          trackDescriptionGeneratedFailed={trackDescriptionGeneratedFailed}
          getAISuggestions={getAISuggestions}
          fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
        />
      </QuestionContentWrapper>
    );
  }

  return (
    <QuestionContentWrapper
      question={question}
      answer={answer}
      highlightUnansweredPriorityQuestions={
        highlightUnansweredPriorityQuestions
      }
      i18nLocale={i18nLocale}
    >
      <SurveyListReferencesQuestion
        questionLabel={question.label}
        allowComponentCreation={allowComponentCreation}
        readonly={readonly}
        references={value}
        currentLocale={currentLocale}
        i18nLocale={i18nLocale}
        surveyId={survey._id}
        questionId={questionId}
        componentId={componentId}
        userFeedback={userFeedback}
        fields={fields}
        identifierFieldName={identifierFieldName}
        hideReadOnlyLabel={hideReadOnlyLabel}
        shouldFetchReferenceSuggestions={shouldFetchReferenceSuggestions}
        additionalFilterOptions={additionalFilterOptions}
        createReference={createReference}
        deleteReference={deleteReference}
        getAISuggestions={getAISuggestions}
        fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
      />
    </QuestionContentWrapper>
  );
};
